import { useState, useRef, useCallback, useEffect } from 'react';
import { drawPoseHud } from '../api/drawPoseHud';
import { convertWebmToMp4 } from '../api/convertToMp4';

/**
 * AI 스켈레톤 및 비디오 프레임을 합성하여 로컬 영상 파일로 녹화/저장하는 커스텀 훅
 */
export function useVideoRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recorderError, setRecorderError] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const animFrameIdRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const recordCanvasRef = useRef(null);

  // 브라우저 지원 MIME 타입 자동 탐색
  const getSupportedMimeType = () => {
    if (typeof MediaRecorder === 'undefined') return '';
    const types = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4;codecs=avc1',
      'video/mp4',
    ];
    return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
  };

  const isCancelingRef = useRef(false);

  const onCompleteRef = useRef(null);
  const autoDownloadRef = useRef(false);
  const getHudDataRef = useRef(null);

  const cleanup = () => {
    if (animFrameIdRef.current) {
      cancelAnimationFrame(animFrameIdRef.current);
      animFrameIdRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setIsRecording(false);
    setRecordingDuration(0);
    setIsConverting(false);
    setConversionProgress(0);
    recordCanvasRef.current = null;
    isCancelingRef.current = false;
    onCompleteRef.current = null;
    autoDownloadRef.current = false;
    getHudDataRef.current = null;
  };

  const startRecording = useCallback(({
    videoElement,
    canvasElement,
    filenamePrefix = 'aimus-pose-record',
    autoDownload = false,
    isMirrored = false,
    getHudData,
    onRecordComplete,
  }) => {
    setRecorderError(null);
    isCancelingRef.current = false;
    onCompleteRef.current = onRecordComplete || null;
    autoDownloadRef.current = autoDownload;
    getHudDataRef.current = getHudData || null;

    if (!videoElement || !canvasElement) {
      setRecorderError('녹화할 비디오 또는 캔버스 소스를 찾을 수 없습니다.');
      return false;
    }

    if (typeof MediaRecorder === 'undefined') {
      setRecorderError('현재 브라우저가 화면 녹화(MediaRecorder) 기능을 지원하지 않습니다.');
      return false;
    }

    try {
      const width = videoElement.videoWidth || canvasElement.width || 1280;
      const height = videoElement.videoHeight || canvasElement.height || 720;

      // 오프스크린 합성 캔버스 준비
      const recCanvas = document.createElement('canvas');
      recCanvas.width = width;
      recCanvas.height = height;
      recordCanvasRef.current = recCanvas;
      const recCtx = recCanvas.getContext('2d');

      // 실시간 프레임 합성 루프
      const renderCompositeFrame = () => {
        if (recCtx && videoElement && canvasElement) {
          recCtx.clearRect(0, 0, width, height);

          if (isMirrored) {
            recCtx.save();
            recCtx.translate(width, 0);
            recCtx.scale(-1, 1);
            recCtx.drawImage(videoElement, 0, 0, width, height);
            recCtx.drawImage(canvasElement, 0, 0, width, height);
            recCtx.restore();
          } else {
            recCtx.drawImage(videoElement, 0, 0, width, height);
            recCtx.drawImage(canvasElement, 0, 0, width, height);
          }

          // 태블릿 기준 각도 및 타이머 HUD 박스 캔버스 오버레이 렌더링
          if (typeof getHudDataRef.current === 'function') {
            const hudData = getHudDataRef.current();
            if (hudData) {
              drawPoseHud(recCtx, {
                width,
                height,
                ...hudData,
              });
            }
          }

          // 우측 하단 워터마크 브랜딩
          recCtx.save();
          recCtx.fillStyle = 'rgba(15, 23, 42, 0.6)';
          recCtx.fillRect(width - 165, height - 36, 155, 26);
          recCtx.fillStyle = '#38bdf8';
          recCtx.font = 'bold 12px Outfit, sans-serif';
          recCtx.fillText('AIMUS AI VISION', width - 153, height - 19);
          recCtx.restore();
        }
        animFrameIdRef.current = requestAnimationFrame(renderCompositeFrame);
      };

      animFrameIdRef.current = requestAnimationFrame(renderCompositeFrame);

      // 캔버스 스트림 생성 (30 FPS)
      const stream = recCanvas.captureStream(30);
      const mimeType = getSupportedMimeType();
      const options = mimeType ? { mimeType, videoBitsPerSecond: 3500000 } : undefined;

      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      recordedChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (!isCancelingRef.current && event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const chunks = recordedChunksRef.current;
        // 취소된 상태가 아니고 수집된 청크가 있을 때 처리
        if (!isCancelingRef.current && chunks.length > 0) {
          const actualMime = recorder.mimeType || mimeType || 'video/webm';
          const rawBlob = new Blob(chunks, { type: actualMime });
          const durationAtStop = recordingDuration;

          let finalBlob = rawBlob;
          let extension = actualMime.includes('mp4') ? 'mp4' : 'webm';

          // mp4로 직접 녹화되지 않은 경우(대부분의 Chrome/Edge) ffmpeg.wasm으로 재인코딩
          if (extension !== 'mp4') {
            try {
              setIsConverting(true);
              setConversionProgress(0);
              finalBlob = await convertWebmToMp4(rawBlob, (ratio) => setConversionProgress(ratio));
              extension = 'mp4';
            } catch (err) {
              console.error('[VideoRecorder] mp4 변환 실패, webm으로 저장합니다:', err);
              finalBlob = rawBlob;
              extension = actualMime.includes('mp4') ? 'mp4' : 'webm';
            } finally {
              setIsConverting(false);
              setConversionProgress(0);
            }
          }

          if (!isCancelingRef.current) {
            const downloadUrl = URL.createObjectURL(finalBlob);
            const filename = `${filenamePrefix}-${Date.now()}.${extension}`;

            // 자동 다운로드 옵션이 켜진 경우에만 직접 다운로드 트리거
            if (autoDownloadRef.current) {
              const a = document.createElement('a');
              a.style.display = 'none';
              a.href = downloadUrl;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }

            // 완료 콜백 전달
            if (typeof onCompleteRef.current === 'function') {
              onCompleteRef.current({
                id: `rec-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                type: 'video',
                url: downloadUrl,
                blob: finalBlob,
                name: filename,
                duration: durationAtStop,
                createdAt: new Date(),
              });
            }
          }
        }
        cleanup();
      };

      recorder.start(200); // 200ms 단위 청크 수집
      setIsRecording(true);
      setRecordingDuration(0);

      // 1초 타이머
      timerIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);

      return true;
    } catch (err) {
      console.error('[VideoRecorder] 녹화 시작 실패:', err);
      setRecorderError('영상 녹화 시작 중 오류가 발생했습니다.');
      cleanup();
      return false;
    }
  }, []);

  const stopRecording = useCallback(() => {
    isCancelingRef.current = false;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    } else {
      cleanup();
    }
  }, []);

  const cancelRecording = useCallback(() => {
    isCancelingRef.current = true;
    recordedChunksRef.current = [];
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    } else {
      cleanup();
    }
  }, []);

  useEffect(() => {
    return () => {
      isCancelingRef.current = true;
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      cleanup();
    };
  }, []);

  // mm:ss 포맷팅 헬퍼
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return {
    isRecording,
    recordingDuration,
    formattedDuration: formatDuration(recordingDuration),
    recorderError,
    isConverting,
    conversionProgress,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
